import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import { ContextActions } from '../components/ContextActions.jsx';
import { CopyableCode, COPYABLE_CODE_CSS } from '../components/CopyableCode.jsx';
import { useToast } from '../components/Toast.jsx';
import { Wizard, WizardStep } from '../components/Wizard.jsx';
import { AddIcon, CheckIcon } from '../components/icons';

export function Projects() {
  const { getToken } = useAuth();
  const api = useMemo(() => createApiClient(getToken), [getToken]);
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [projects, setProjects] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);

  // Card 071 — deep-link to the create wizard.  Empty-state CTAs (the
  // ProjectPicker's "no projects", the Home dashboard's first-project
  // nudge) navigate here with `?create=1` so the user lands directly
  // on the wizard instead of first having to find the New button.
  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setShowWizard(true);
      setSearchParams((sp) => {
        const next = new URLSearchParams(sp);
        next.delete('create');
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  // Auto-minted embed plaintext returned on the create response —
  // surface immediately so the operator can copy before navigating
  // away.  This lives on the page (not the wizard) so it persists
  // after the wizard closes.
  const [justMintedEmbed, setJustMintedEmbed] = useState(null);
  // Cross-project help signal — drives the context-actions strip when
  // there's something for the operator to triage across all projects.
  const [helpOpen, setHelpOpen] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, help] = await Promise.all([
        api('/v1/admin/projects'),
        api('/v1/admin/help/requests?status=open').catch(() => null),
      ]);
      const list = Array.isArray(data) ? data : (data?.items || []);
      setProjects(list);
      setHelpOpen(help?.items?.length || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Build the contextual actions for this page.  Empty list = nothing
  // pressing, ContextActions collapses silently.
  const actions = useMemo(() => {
    const out = [];
    if (!loading && projects.length === 0) {
      out.push({
        id: 'first-project',
        title: 'Create your first project',
        body: 'A project scopes API keys, tours, content, and the help inbox. Use Create project to ship one.',
        cta: 'Create project',
        onClick: () => setShowWizard(true),
        tone: 'primary',
      });
    }
    if (helpOpen > 0) {
      out.push({
        id: 'open-help',
        title: helpOpen === 1 ? '1 open help request' : `${helpOpen} open help requests`,
        body: 'Across all projects you can see. Triage from the inbox.',
        cta: 'Open inbox',
        to: '/help',
        tone: 'warn',
      });
    }
    return out;
  }, [loading, projects.length, helpOpen]);

  return (
    <div className="page">
      <style>{COPYABLE_CODE_CSS}</style>
      <div className="page__title">
        <div>
          <h1>Projects</h1>
          <p className="page__subtitle">
            Scoping containers inside your tenant — one per distinct
            area you&apos;re running Spotlight on (your marketing site, your
            product app, your admin console). Tours, content, themes,
            and checklists live inside a project.
          </p>
        </div>
        <button
          type="button"
          data-ui-name="projects-new-button"
          className="btn btn--primary"
          onClick={() => setShowWizard(true)}
        >
          <AddIcon /> Create project
        </button>
      </div>

      <ContextActions actions={actions} />

      {showWizard && (
        <CreateProjectWizard
          api={api}
          onCancel={() => setShowWizard(false)}
          onCreated={(created) => {
            setShowWizard(false);
            if (created?.default_embed_key) {
              setJustMintedEmbed({
                key: created.default_embed_key,
                prefix: created.default_embed_prefix || '',
                projectName: created.name || '',
              });
            }
            showToast({
              title: `Created project "${created?.name || ''}"`,
              variant: 'success',
            });
            load();
          }}
        />
      )}

      {justMintedEmbed && (
        <FreshDefaultEmbed
          minted={justMintedEmbed}
          onDismiss={() => setJustMintedEmbed(null)}
        />
      )}

      {loading && (
        <div className="state">
          <div className="state__title">Loading your projects…</div>
        </div>
      )}
      {error && !loading && (
        <div className="state state--error">
          <div className="state__title">Couldn&apos;t load projects</div>
          <div>{error}</div>
        </div>
      )}
      {!loading && !error && projects.length === 0 && (
        <div className="state">
          <div className="state__title">No projects in this tenant yet</div>
          <div>
            Use <strong>Create project</strong> above to ship your first —
            its slug drives the URL you&apos;ll see in the sidebar
            (<code>/projects/&lt;slug&gt;</code>), and the first project
            you create becomes the tenant&apos;s default automatically.
            Until one exists, API calls that need a project scope
            (tours, content, themes) will return 404.
          </div>
        </div>
      )}
      {!loading && !error && projects.length > 0 && (
        <div className="card-grid">
          {projects.map((p) => (
            <ProjectCard key={p.project_id} project={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectCard({ project }) {
  return (
    <Link to={`/projects/${project.slug}`} className="quick-card">
      <div className="quick-card__title">
        {project.name}
        {project.is_default && (
          <span className="badge badge--default" style={{ marginLeft: 8 }}>
            Default
          </span>
        )}
      </div>
      <div className="quick-card__body">
        <div><code>{project.slug}</code></div>
        {project.description && (
          <p style={{ marginTop: 6 }}>{project.description}</p>
        )}
      </div>
    </Link>
  );
}

/**
 * Three-step "Create project" wizard (card 059).
 *
 *   1. Name + slug — auto-derive slug from name with an override
 *      flag so the operator can shorten it for the URL.
 *   2. Description (optional) — internal blurb.
 *   3. Review + create — the create endpoint also auto-mints a
 *      default embed; that plaintext surfaces on the page after
 *      the wizard closes, never inside the wizard (no plaintext
 *      in sessionStorage drafts).
 */
function CreateProjectWizard({ api, onCancel, onCreated }) {
  const initial = {
    name: '',
    slug: '',
    description: '',
    slugDirty: false,
  };

  function deriveSlug(v) {
    return v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
  }

  return (
    <Wizard
      id="create-project"
      title="Create a project"
      subtitle="A project scopes API keys, tours, content, and the help inbox."
      initialValues={initial}
      submitLabel="Create project"
      onCancel={onCancel}
      onSubmit={async (values) => {
        const created = await api('/v1/admin/projects', {
          method: 'POST',
          body: JSON.stringify({
            name: values.name.trim(),
            slug: values.slug.trim(),
            description: values.description.trim(),
            is_default: false,
          }),
        });
        onCreated(created);
      }}
    >
      <WizardStep
        title="Name + slug"
        isValid={(v) => v.name.trim().length > 0 && /^[a-z0-9-]+$/.test(v.slug.trim())}
      >
        {({ values, setMany, set }) => (
          <div className="wiz-stack">
            <label className="field">
              <span className="field__label">Display name</span>
              <input
                className="input"
                value={values.name}
                onChange={(e) => {
                  const v = e.target.value;
                  setMany({
                    ...values,
                    name: v,
                    slug: values.slugDirty ? values.slug : deriveSlug(v),
                  });
                }}
                placeholder="Acme Production"
              />
            </label>
            <label className="field">
              <span className="field__label">Slug</span>
              <input
                className="input"
                value={values.slug}
                onChange={(e) => setMany({ ...values, slug: e.target.value, slugDirty: true })}
                placeholder="acme-production"
                pattern="[a-z0-9-]+"
              />
              <p className="wiz-hint">
                Drives the URL — <code>/projects/{values.slug || '<slug>'}</code>.
                Lowercase letters, digits, and hyphens only.
              </p>
            </label>
          </div>
        )}
      </WizardStep>

      <WizardStep title="Description (optional)">
        {({ values, set }) => (
          <div className="wiz-stack">
            <label className="field">
              <span className="field__label">Description</span>
              <input
                className="input"
                value={values.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="The public production app."
              />
              <p className="wiz-hint">Internal blurb — surfaces on the projects index.</p>
            </label>
          </div>
        )}
      </WizardStep>

      <WizardStep title="Review">
        {({ values }) => (
          <div className="wiz-stack">
            <p className="wiz-hint" style={{ margin: 0 }}>
              We&apos;ll create the project and auto-mint a default
              public embed key. The embed key plaintext will be shown
              once on this page after creation.
            </p>
            <dl className="wiz-review">
              <dt>Name</dt>
              <dd>{values.name}</dd>
              <dt>Slug</dt>
              <dd><code>{values.slug}</code></dd>
              <dt>Description</dt>
              <dd>{values.description || <em>none</em>}</dd>
            </dl>
          </div>
        )}
      </WizardStep>
    </Wizard>
  );
}

function FreshDefaultEmbed({ minted, onDismiss }) {
  return (
    <div className="state" style={{ marginTop: 16, textAlign: 'left', background: '#ecfdf5', borderColor: '#86efac' }}>
      <div className="state__title">
        Default site embed minted for {minted.projectName || 'this project'}
      </div>
      <p style={{ margin: '6px 0 8px', fontSize: 13 }}>
        We&apos;ve created a system-owned <code>pk_live_*</code> key called
        <strong> Default site embed</strong>. Paste it into your SDK&apos;s
        <code> data-api-key</code> attribute. It&apos;s safe to ship in page
        source — origin-locking on the Sites page is the security
        boundary. <strong>This is the only time the secret is shown</strong>;
        if you lose it, mint another from the project&apos;s API keys page.
      </p>
      <CopyableCode value={minted.key} label="embed key" />
      <button type="button" className="btn btn--ghost" onClick={onDismiss} style={{ marginTop: 10 }}>
        <CheckIcon /> I&apos;ve saved it
      </button>
    </div>
  );
}
